import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import RuleFlowVisualizer from './RuleFlowVisualizer';
import { v4 as uuidv4 } from 'uuid';
import { OfferRule } from '@/hooks/useOfferRules';
import RuleConditionRow, { RuleCondition } from '@/components/RuleConditionRow';
import { availableFields, availableOperators } from '@/utils/ruleDefinitions';
import { combinatorJoiner } from '@/utils/ruleFormat';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import ActionConfigurationRow, { RuleAction } from '@/components/ActionConfigurationRow';
import type { Tables } from '@/integrations/supabase/types';
import { PlusCircle, Trash2, Layers, Gift } from 'lucide-react';

// Add ConditionGroup interface for type safety
interface ConditionGroup {
  id?: string;
  combinator: 'AND' | 'OR';
  conditions: RuleCondition[];
}

interface RuleBuilderCanvasProps {
  onSave: (rule: OfferRule) => void;
  onCancel: () => void;
  initialData: OfferRule | null;
  offerTemplates: Tables<'offer_templates'>[];
}

/**
 * Small-screen strategy (deliberate): the previous 3-pane, fixed-height
 * `ResizablePanelGroup` was desktop-only by construction and broke below ~900px.
 * Rule authoring is an owner task done on a laptop or phone, so this is a single
 * responsive column instead: condition rows reshape (stacked on phones, inline
 * from `sm`), groups are explicit labeled containers, and AND/OR combinators are
 * spelled out as controls and dividers. Genuinely usable at 360px, not a shrunk
 * canvas. A live plain-language Preview tab mirrors the real logic.
 */
const RuleBuilderCanvas: React.FC<RuleBuilderCanvasProps> = ({ onSave, onCancel, initialData, offerTemplates }) => {
  const [rule, setRule] = useState<OfferRule>({
    id: uuidv4(),
    name: 'New Rule',
    description: '',
    priority: 1,
    is_active: true,
    tags: [],
    group_combinator: 'AND',
    condition_groups: [{ id: uuidv4(), combinator: 'AND', conditions: [] }],
    actions: [],
    json_representation: {},
  });
  const [isJsonDialogOpen, setIsJsonDialogOpen] = useState(false);
  const [jsonOutput, setJsonOutput] = useState('');

  // Effect to update rule state when initialData changes
  useEffect(() => {
    if (initialData) {
      // Ensure condition groups have IDs if they don't exist
      const conditionGroupsWithIds = initialData.condition_groups.map(group => ({
        ...group,
        id: group.id || uuidv4(),
        conditions: group.conditions.map(condition => ({
          ...condition,
          id: condition.id || uuidv4()
        }))
      }));

      setRule({
        ...initialData,
        condition_groups: conditionGroupsWithIds
      });
    }
  }, [initialData]);

  const generateRuleJson = () => {
    return {
      name: rule.name,
      description: rule.description,
      priority: rule.priority,
      conditions: {
        combinator: rule.group_combinator,
        groups: rule.condition_groups.map(group => ({
          combinator: group.combinator,
          conditions: group.conditions.map(condition => ({
            field: condition.field.replace('.', '_'),
            operator: condition.operator,
            value: condition.value,
          }))
        }))
      },
      actions: rule.actions.map(action => ({
        type: action.type,
        params: action.value
      }))
    };
  };

  const handleSaveRule = () => {
    const ruleWithJson = {
      ...rule,
      json_representation: generateRuleJson(),
    };
    onSave(ruleWithJson);
  };

  const handleAddConditionGroup = () => {
    setRule((prevRule) => ({
      ...prevRule,
      condition_groups: [
        ...prevRule.condition_groups,
        { id: uuidv4(), combinator: 'AND', conditions: [] },
      ],
    }));
  };

  const handleRemoveConditionGroup = (groupId: string) => {
    setRule((prevRule) => ({
      ...prevRule,
      condition_groups: prevRule.condition_groups.filter((group) => group.id !== groupId),
    }));
  };

  const handleAddCondition = (groupId: string) => {
    const newCondition: RuleCondition = {
      id: uuidv4(),
      field: availableFields[0].value,
      operator: availableOperators.text[0].value,
      value: null,
    };
    setRule((prevRule) => ({
      ...prevRule,
      condition_groups: prevRule.condition_groups.map((group) =>
        group.id === groupId
          ? { ...group, conditions: [...group.conditions, newCondition] }
          : group
      ),
    }));
  };

  const handleUpdateCondition = (groupId: string, updatedCondition: RuleCondition) => {
    setRule((prevRule) => ({
      ...prevRule,
      condition_groups: prevRule.condition_groups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              conditions: group.conditions.map((cond) =>
                cond.id === updatedCondition.id ? updatedCondition : cond
              ),
            }
          : group
      ),
    }));
  };

  const handleRemoveCondition = (groupId: string, conditionId: string) => {
    setRule((prevRule) => ({
      ...prevRule,
      condition_groups: prevRule.condition_groups.map((group) =>
        group.id === groupId
          ? { ...group, conditions: group.conditions.filter((cond) => cond.id !== conditionId) }
          : group
      ),
    }));
  };

  const handleUpdateConditionGroup = (groupId: string, updates: Partial<ConditionGroup>) => {
    setRule((prevRule) => ({
      ...prevRule,
      condition_groups: prevRule.condition_groups.map((group) =>
        group.id === groupId ? { ...group, ...updates } : group
      ),
    }));
  };

  const handleAddAction = () => {
    const newAction: RuleAction = {
      id: uuidv4(),
      type: 'assign_offer',
      value: { offer_template_id: '' },
    };
    setRule({ ...rule, actions: [...rule.actions, newAction] });
  };

  const handleUpdateAction = (updatedAction: RuleAction) => {
    setRule({ ...rule, actions: rule.actions.map(act => act.id === updatedAction.id ? updatedAction : act) });
  };

  const handleRemoveAction = (actionId: string) => {
    setRule({ ...rule, actions: rule.actions.filter(act => act.id !== actionId) });
  };

  const handleExportJson = () => {
    const formattedRule = generateRuleJson();
    setJsonOutput(JSON.stringify(formattedRule, null, 2));
    setIsJsonDialogOpen(true);
  };

  return (
    <>
      <Card>
        <Tabs defaultValue="editor" className="w-full">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Layers className="h-5 w-5 text-brand-red" />
                {initialData ? 'Edit rule' : 'Build a rule'}
              </CardTitle>
              <TabsList className="self-start">
                <TabsTrigger value="editor">Build</TabsTrigger>
                <TabsTrigger value="visualizer">Preview</TabsTrigger>
              </TabsList>
            </div>
            <p className="text-sm text-muted-foreground">
              Conditions decide who qualifies. Actions decide what they get. Groups let you mix
              ALL and ANY logic.
            </p>
          </CardHeader>

          <CardContent>
            <TabsContent value="editor" className="mt-0 space-y-6">
              {/* Rule settings */}
              <section className="rounded-lg border bg-muted/40 p-4">
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-brand-gray">
                  Rule settings
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="rule-name">Rule name</Label>
                    <Input
                      id="rule-name"
                      value={rule.name}
                      onChange={(e) => setRule({ ...rule, name: e.target.value })}
                      placeholder="e.g. Win back lapsing regulars"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rule-priority">Priority (1 = highest)</Label>
                    <Input
                      id="rule-priority"
                      type="number"
                      value={rule.priority}
                      onChange={(e) => setRule({ ...rule, priority: Number(e.target.value) })}
                      min="1"
                    />
                  </div>
                </div>
              </section>

              {/* Condition groups */}
              <section className="space-y-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Conditions</h3>
                    <p className="text-xs text-muted-foreground">Who this rule applies to.</p>
                  </div>
                  {rule.condition_groups.length > 1 && (
                    <div className="flex items-center gap-2">
                      <Label htmlFor="group-combinator" className="text-xs text-muted-foreground">
                        Between groups
                      </Label>
                      <Select
                        value={rule.group_combinator}
                        onValueChange={(value) =>
                          setRule({ ...rule, group_combinator: value as 'AND' | 'OR' })
                        }
                      >
                        <SelectTrigger id="group-combinator" className="w-[150px]">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AND">Match ALL (AND)</SelectItem>
                          <SelectItem value="OR">Match ANY (OR)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {rule.condition_groups.length === 0 && (
                  <div className="rounded-lg border-2 border-dashed border-border bg-muted/40 py-8 text-center">
                    <Layers className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                    <p className="mb-1 text-sm font-medium text-foreground">No condition groups yet</p>
                    <p className="mb-4 text-xs text-muted-foreground">
                      Add a group to start describing who qualifies.
                    </p>
                    <Button onClick={handleAddConditionGroup} size="sm">
                      <PlusCircle className="mr-2 h-4 w-4" /> Add your first group
                    </Button>
                  </div>
                )}

                {rule.condition_groups.map((group, groupIndex) => (
                  <React.Fragment key={group.id}>
                    {groupIndex > 0 && (
                      <div className="flex items-center gap-3 py-1" aria-hidden="true">
                        <span className="h-px flex-1 bg-border" />
                        <span className="rounded-full bg-muted px-3 py-0.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                          {combinatorJoiner(rule.group_combinator)}
                        </span>
                        <span className="h-px flex-1 bg-border" />
                      </div>
                    )}
                    <div className="rounded-lg border bg-card p-4 shadow-sm">
                      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-2.5">
                          <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-status-info/10 text-sm font-semibold text-status-info">
                            {groupIndex + 1}
                          </span>
                          <div>
                            <h4 className="text-sm font-semibold">Group {groupIndex + 1}</h4>
                            <p className="text-xs text-muted-foreground">
                              {group.conditions.length} condition{group.conditions.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Label htmlFor={`group-combinator-${group.id}`} className="text-xs text-muted-foreground">
                            Match
                          </Label>
                          <Select
                            value={group.combinator}
                            onValueChange={(value) => handleUpdateConditionGroup(group.id!, { combinator: value as 'AND' | 'OR' })}
                          >
                            <SelectTrigger id={`group-combinator-${group.id}`} className="w-[132px]">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="AND">ALL (AND)</SelectItem>
                              <SelectItem value="OR">ANY (OR)</SelectItem>
                            </SelectContent>
                          </Select>
                          {rule.condition_groups.length > 1 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Remove group ${groupIndex + 1}`}
                              onClick={() => handleRemoveConditionGroup(group.id!)}
                              className="text-muted-foreground hover:text-status-danger"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        {group.conditions.length > 0 ? (
                          group.conditions.map((condition) => (
                            <RuleConditionRow
                              key={condition.id}
                              condition={condition}
                              onUpdate={(updatedCondition) => handleUpdateCondition(group.id!, updatedCondition)}
                              onRemove={(conditionId) => handleRemoveCondition(group.id!, conditionId)}
                              availableFields={availableFields}
                              availableOperators={availableOperators}
                            />
                          ))
                        ) : (
                          <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">
                            No conditions in this group yet.
                          </p>
                        )}
                        <Button
                          onClick={() => handleAddCondition(group.id!)}
                          variant="outline"
                          size="sm"
                          className="w-full"
                        >
                          <PlusCircle className="mr-2 h-4 w-4" /> Add condition
                        </Button>
                      </div>
                    </div>
                  </React.Fragment>
                ))}

                {rule.condition_groups.length > 0 && (
                  <Button onClick={handleAddConditionGroup} variant="outline" className="w-full sm:w-auto">
                    <Layers className="mr-2 h-4 w-4" /> Add condition group
                  </Button>
                )}
              </section>

              {/* Actions */}
              <section className="space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Actions</h3>
                  <p className="text-xs text-muted-foreground">What qualifying customers get.</p>
                </div>
                {rule.actions.length > 0 ? (
                  <div className="space-y-2">
                    {rule.actions.map((action) => (
                      <ActionConfigurationRow
                        key={action.id}
                        action={action}
                        onUpdate={handleUpdateAction}
                        onRemove={handleRemoveAction}
                        offerTemplates={offerTemplates}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border-2 border-dashed border-border bg-muted/40 py-8 text-center">
                    <Gift className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                    <p className="mb-1 text-sm font-medium text-foreground">No actions yet</p>
                    <p className="mb-4 text-xs text-muted-foreground">
                      Add an action to say what happens when the conditions match.
                    </p>
                    <Button onClick={handleAddAction} size="sm">
                      <PlusCircle className="mr-2 h-4 w-4" /> Add your first action
                    </Button>
                  </div>
                )}
                {rule.actions.length > 0 && (
                  <Button onClick={handleAddAction} variant="outline" className="w-full sm:w-auto">
                    <PlusCircle className="mr-2 h-4 w-4" /> Add another action
                  </Button>
                )}
              </section>
            </TabsContent>

            <TabsContent value="visualizer" className="mt-0">
              <RuleFlowVisualizer rule={rule} offerTemplates={offerTemplates} />
            </TabsContent>
          </CardContent>

          {/* Footer: reverse column on phones so Save sits under the thumb; row on sm+. */}
          <div className="flex flex-col-reverse gap-2 border-t p-4 sm:flex-row sm:items-center sm:justify-end">
            <Button variant="ghost" onClick={handleExportJson} className="sm:mr-auto">
              Export as JSON
            </Button>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveRule}
              className="bg-brand-red hover:bg-brand-red/90 text-brand-white"
            >
              Save rule
            </Button>
          </div>
        </Tabs>
      </Card>

      <Dialog open={isJsonDialogOpen} onOpenChange={setIsJsonDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Rule as JSON</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="mb-2 text-sm text-muted-foreground">
              This JSON output can be used by backend systems.
            </p>
            <Textarea
              readOnly
              value={jsonOutput}
              className="h-64 bg-muted font-mono text-xs"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RuleBuilderCanvas;
